import { Type, TypeSpec } from 'src/models/Sidekick/SidekickModuleSpec';
import { builtin } from '../../../actions/utils';
import { isObject } from 'lodash';

export const typeSpecJSON = (t: TypeSpec | null): string => {
  return JSON.stringify(tsjson(t), null, 2);
};

const tsjson = (t: TypeSpec | null): unknown => {
  if (t?.type === Type.array) {
    return [tsjson(t.items || null)];
  }
  const obj = zeroVal(t);
  if (t?.props == null) {
    return obj;
  }
  for (const pk of Object.keys(t.props || [])) {
    if (pk == null) continue;
    const prop = t.props[pk];
    if (prop == null) continue;
    switch (prop.type) {
      case Type.array:
        obj[pk] = [tsjson(prop.items || null)];
        break;
      case Type.object:
        obj[pk] = tsjson(prop);
        break;
      default:
        obj[pk] = zeroVal(prop);
    }
  }
  return obj;
};

const zeroVal = (t: TypeSpec | null): any => {
  switch (t?.type) {
    case Type.number:
      return 0;
    case Type.string:
      if (t?.enum != null) {
        return `One Of '${[t.enum || []].join(',')}'`;
      }
      return '';
    case Type.bool:
      return false;
    case Type.object:
      return {};
    default:
      return '';
  }
};

export const getExternalActionInputProperties = (
  tmpMems: Record<string, string>,
  inputTypeSpec: TypeSpec,
  typeSpecsMap: Record<string, TypeSpec>,
  compatibilityMatch: boolean
) => {
  const mems: Record<string, string> = {};
  for (const mk of Object.keys(tmpMems)) {
    const mem = tmpMems[mk];
    if (mem == null) continue;
    let spec = typeSpecsMap[mem];
    if (spec == null) continue;
    if (!builtin(spec.type)) {
      spec = typeSpecsMap[spec.type];
    }
    if (hasTypeInSpec(spec, inputTypeSpec, typeSpecsMap, compatibilityMatch)) {
      mems[mk] = mem;
    }
  }
  return mems;
};

export const hasTypeInSpec = (
  spec: TypeSpec | undefined,
  target: Type | TypeSpec,
  types: Record<string, TypeSpec>,
  compatibilityMatch: boolean = false
): boolean => {
  if (spec == null) {
    return false;
  }
  if (!builtin(spec.type)) {
    spec = types[spec.type];
  }
  for (const pk of Object.keys(spec?.props || {})) {
    if (spec?.props == null) {
      continue;
    }
    let propSpec = spec.props[pk];
    if (propSpec != null && !builtin(propSpec.type)) {
      propSpec = types[propSpec.type];
    }
    const hasNested = hasTypeInSpec(propSpec, target, types, compatibilityMatch);
    if (compatibilityMatch ? compatibleEquals(propSpec, target) || hasNested : propSpec?.type == target || hasNested) {
      return true;
    }
  }
  return compatibilityMatch ? compatibleEquals(spec, target) : spec?.type == target;
};

export const compatibleEquals = <T>(strict: T, loose: T): boolean => {
  const strictKeys = isObject(strict) ? Object.keys(strict as object) : [];
  const looseKeys = isObject(loose) ? Object.keys(loose as object) : [];

  // We don't care about the extra key "null" when it's present on the loose
  // side. The loose side's value for null does not affect comptability with
  // the strict side.
  if (strictKeys.length !== looseKeys.filter(key => key !== 'null').length) {
    return false;
  }
  for (const key of strictKeys) {
    const strictVal = strict[key as keyof T];
    const looseVal = loose[key as keyof T];
    const areObjects = isObject(strictVal) && isObject(looseVal);
    if (
      (areObjects && !compatibleEquals(strictVal, looseVal)) ||
      (!areObjects && key !== 'null' && strictVal !== looseVal)
    ) {
      return false;
    }

    // Only when the strictVal can be null and the looseVal is never null
    // do we want to say the two are not compatible
    if (!areObjects && key === 'null' && strictVal && !looseVal) {
      return false;
    }
  }
  return true;
};
